// src/pages/employee/EmployeeLeave.jsx
// Past dates are disabled on all date inputs.
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useMyLeaveRequests,
  submitLeaveRequest,
  useAllUsers,
  getTodayStr,
} from "../../hooks/useFirestore";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function EmployeeLeave() {
  const { userProfile } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);
  const uid = userProfile?.uid;

  const { data: requests, loading } = useMyLeaveRequests(uid);
  const { data: users } = useAllUsers();

  const myTeam = users.filter(
    u => u.teamId === userProfile?.teamId && u.uid !== uid,
  );

  const today = getTodayStr();

  function getTomorrowStr() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  }

  const tomorrow = getTomorrowStr();

  const [form, setForm] = useState({
    leaveType: "",
    halfDaySession: "first_half",
    deductFrom: "casual",
    fromDate: "",
    toDate: "",
    reason: "",
    handoverTo: "",
  });

  const effectiveLeaveType = form.leaveType === "half_day" ? form.deductFrom : form.leaveType;
  const minFromDate = effectiveLeaveType === "casual" ? tomorrow : today;

  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [totalDays, setTotalDays] = useState(0);
  const [medCertificate, setMedCertificate] = useState(null);

  function handleMedCertChange(e) {
    const file = e.target.files?.[0];
    if (!file) {
      setMedCertificate(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMsg("❌ File size too large. Please upload a medical certificate smaller than 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      setMedCertificate({
        name: file.name,
        data: evt.target.result,
      });
    };
    reader.readAsDataURL(file);
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (updated.leaveType === "half_day") {
        if (name === "fromDate") {
          updated.toDate = value;
        }
        setTotalDays(0.5);
      } else if (updated.fromDate && updated.toDate) {
        const from = new Date(updated.fromDate);
        const to = new Date(updated.toDate);

        let days = 0;
        let current = new Date(from);

        while (current <= to) {
          const day = current.getDay();
          if (day !== 0 && day !== 6) { // Exclude Saturday (6) & Sunday (0)
            days++;
          }
          current.setDate(current.getDate() + 1);
        }

        setTotalDays(days);
      } else {
        setTotalDays(0);
      }

      return updated;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (!form.leaveType) {
      setMsg("❌ Please select a leave type.");
      return;
    }

    const isHalfDay = form.leaveType === "half_day";
    const actualLeaveType = isHalfDay ? form.deductFrom : form.leaveType;

    // 24-hour prior notice guard for Casual Leave
    if (actualLeaveType === "casual" && form.fromDate < tomorrow) {
      setMsg("❌ Casual Leave must be applied at least 24 hours in advance (start date must be tomorrow or later).");
      return;
    }

    // Client-side past-date guard
    if (form.fromDate < today) {
      setMsg("❌ Leave start date cannot be in the past.");
      return;
    }

    if (!isHalfDay && form.toDate < form.fromDate) {
      setMsg("❌ End date must be on or after the start date.");
      return;
    }

    let calculatedDays = isHalfDay ? 0.5 : 0;
    if (!isHalfDay) {
      const from = new Date(form.fromDate);
      const to = new Date(form.toDate);

      let curr = new Date(from);
      while (curr <= to) {
        const day = curr.getDay();
        if (day !== 0 && day !== 6) {
          calculatedDays++;
        }
        curr.setDate(curr.getDate() + 1);
      }
    }

    if (calculatedDays === 0) {
      setMsg("❌ Selected date range contains no working days.");
      return;
    }

    // 📄 Force Medical Certificate ONLY for Sick Leave exceeding 3 working days
    const isMedicalCertRequired = actualLeaveType === "sick" && calculatedDays > 3;
    if (isMedicalCertRequired && !medCertificate) {
      setMsg("❌ Medical Certificate is required for Sick Leave requests longer than 3 working days.");
      return;
    }

    // ✅ Current leave balance
    const currentBalance = bal[actualLeaveType] || 0;

    // ✅ Prevent negative leave balance
    if (calculatedDays > currentBalance) {
      setMsg(
        `❌ Leave not applicable. Only ${currentBalance} ${actualLeaveType} leave(s) remaining.`
      );
      return;
    }

    setSaving(true);

    try {
      const handoverUser = myTeam.find((u) => u.uid === form.handoverTo);

      await submitLeaveRequest(userProfile, {
        ...form,
        leaveType: actualLeaveType,
        isHalfDay,
        halfDaySession: isHalfDay ? form.halfDaySession : null,
        deductFrom: actualLeaveType,
        totalDays: calculatedDays,
        toDate: isHalfDay ? form.fromDate : form.toDate,
        handoverToName: handoverUser?.displayName || "",
        medicalCertificateName: medCertificate?.name || null,
        medicalCertificateData: medCertificate?.data || null,
      });

      setMsg("✅ Leave request submitted successfully!");

      setForm({
        leaveType: "",
        halfDaySession: "first_half",
        deductFrom: "casual",
        fromDate: "",
        toDate: "",
        reason: "",
        handoverTo: "",
      });
      setMedCertificate(null);
      setTotalDays(0);
    } catch (err) {
      setMsg("❌ " + err.message);
    }

    setSaving(false);
  }

  const bal = userProfile?.leaveBalance    || {};
  const ent = userProfile?.leaveEntitlement || {};

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div className="page-header">
        <h1 className="page-title">My Leaves</h1>
      </div>

      {/* Leave balance cards */}
      <div className="leave-balance-row">
        {[
          { type: "casual", label: "Casual Leave",  color: "blue"  },
          { type: "sick",   label: "Sick Leave",    color: "green" },
          { type: "annual", label: "Annual Leave",  color: "amber" },
        ].map(({ type, label, color }) => (
          <div key={type} className={`leave-balance-card leave-balance-card--${color}`}>
            <span className="lbc-label">{label}</span>
            <span className="lbc-balance">
  {Math.max(0, bal[type] ?? 0)}
</span>
            <span className="lbc-sub">
              {(ent[type] || 0) - (bal[type] || 0)} used of {ent[type] || 0}
            </span>
            <div className="lbc-bar">
              <div
                className="lbc-fill"
                style={{
                  width: ent[type] > 0
                    ? `${Math.round(((ent[type] - bal[type]) / ent[type]) * 100)}%`
                    : "0%",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="two-col-layout">
        {/* Apply form */}
        <div className="form-card">
          <h3 className="form-card-title">Apply for Leave</h3>
          {msg && (
            <div className={msg.startsWith("✅") ? "form-success" : "form-error"}>
              {msg}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Leave Type *</label>
              <select name="leaveType" value={form.leaveType} onChange={handleChange} required>
                <option value="">-- Select Leave Type --</option>
                <option value="casual">Casual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="annual">Annual Leave</option>
                <option value="half_day">Half Day Leave (0.5 Day)</option>
                <option value="earned">Earned / Annual Leave</option>
                <option value="maternity">Maternity Leave</option>
                <option value="paternity">Paternity Leave</option>
                <option value="comp_off">Comp Off</option>
                <option value="loss_of_pay">Loss Of Pay (LOP)</option>
                <option value="marriage">Marriage Leave</option>
                <option value="bereavement">Bereavement Leave</option>
                <option value="wfh">Work From Home (WFH)</option>
              </select>
              {effectiveLeaveType === "casual" && (
                <div style={{ fontSize: "0.8rem", color: "#2563eb", marginTop: "0.25rem" }}>
                  ℹ️ Casual Leave must be applied at least 24 hours in advance (tomorrow or later).
                </div>
              )}
            </div>

            {form.leaveType === "half_day" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "1rem" }}>
                <div className="form-group">
                  <label>Half Day Session *</label>
                  <select name="halfDaySession" value={form.halfDaySession} onChange={handleChange} required>
                    <option value="first_half">☀️ First Half (Morning: 09:00 AM – 01:30 PM)</option>
                    <option value="second_half">🌙 Second Half (Afternoon: 01:30 PM – 06:00 PM)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Deduct From Balance *</label>
                  <select name="deductFrom" value={form.deductFrom} onChange={handleChange} required>
                    <option value="casual">Casual Leave ({bal.casual ?? 0} remaining)</option>
                    <option value="sick">Sick Leave ({bal.sick ?? 0} remaining)</option>
                  </select>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>From *</label>
                <input
                  name="fromDate"
                  type="date"
                  value={form.fromDate}
                  onChange={handleChange}
                  required
                  min={minFromDate}
                />
              </div>

              {form.leaveType !== "half_day" && (
                <div className="form-group">
                  <label>To *</label>
                  <input
                    name="toDate"
                    type="date"
                    value={form.toDate}
                    onChange={handleChange}
                    required
                    min={form.fromDate || minFromDate}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Days (Excl. Sat &amp; Sun)</label>
                <input
                  type="text"
                  value={totalDays > 0 ? `${totalDays} Day${totalDays > 1 ? "s" : ""}` : ""}
                  readOnly
                  style={{ fontWeight: 600, color: effectiveLeaveType === "sick" && totalDays > 3 ? "#d48806" : "inherit" }}
                />
              </div>
            </div>

            {form.fromDate && (form.toDate || form.leaveType === "half_day") && (
              <div
                style={{
                  margin: "0.5rem 0 1rem",
                  padding: "0.6rem 0.85rem",
                  borderRadius: "8px",
                  background: effectiveLeaveType === "sick" && totalDays > 3 ? "#fffbe6" : "#f6ffed",
                  border: effectiveLeaveType === "sick" && totalDays > 3 ? "1px solid #ffe58f" : "1px solid #b7eb8f",
                  fontSize: "0.85rem",
                  color: "#374151",
                }}
              >
                <strong>Calculated Duration:</strong> {totalDays} Working Day{totalDays > 1 ? "s" : ""} {form.leaveType === "half_day" ? `(${form.halfDaySession === "second_half" ? "Second Half 🌙" : "First Half ☀️"} · Deducting 0.5 from ${effectiveLeaveType === "sick" ? "Sick Leave" : "Casual Leave"})` : "(excl. Saturdays & Sundays)"}
                {effectiveLeaveType === "sick" && totalDays > 3 && (
                  <div style={{ color: "#d48806", marginTop: "0.3rem", fontWeight: 600 }}>
                    ⚠️ Medical Certificate required (Sick Leave exceeds 3 working days).
                  </div>
                )}
              </div>
            )}

            {effectiveLeaveType === "sick" && totalDays > 3 && (
              <div className="form-group" style={{ marginBottom: "1rem" }}>
                <label style={{ color: "#d48806", fontWeight: 600 }}>
                  Upload Medical Certificate * (PDF, Image, Doc &lt; 5MB)
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleMedCertChange}
                  required
                  style={{ width: "100%", padding: "0.4rem", border: "1px solid #ffe58f", borderRadius: "6px" }}
                />
                {medCertificate && (
                  <span style={{ fontSize: "0.82rem", color: "#16a34a", marginTop: "0.25rem", display: "block" }}>
                    ✓ Attached: {medCertificate.name}
                  </span>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Reason *</label>
              <textarea
                name="reason"
                value={form.reason}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Reason for leave…"
              />
            </div>
            <div className="form-group">
              <label>Handover To</label>
              <select name="handoverTo" value={form.handoverTo} onChange={handleChange}>
                <option value="">— Select colleague —</option>
                {myTeam.map(u => (
                  <option key={u.uid} value={u.uid}>{u.displayName}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Submitting…" : "Submit Request"}
            </button>
          </form>
        </div>

        {/* Leave history */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3 className="card-title">Leave History</h3>
          </div>
          {loading ? (
            <div className="card-loading">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="card-empty">No leave requests yet.</div>
          ) : (
            <div className="leave-history-list">
              {requests.map(r => (
                <div key={r.id} className="leave-history-item">
                  <div className="leave-history-header">
                    <span className="leave-history-type" style={{ textTransform: "capitalize" }}>
                      {r.isHalfDay ? `Half Day Leave (${r.halfDaySession === "second_half" ? "Second Half 🌙" : "First Half ☀️"})` : `${r.leaveType} Leave`}
                    </span>
                    <span className={`status-pill status-pill--${
                      r.status === "approved" ? "green" : r.status === "rejected" ? "red" : "amber"
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  <div
                    className="leave-history-dates"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      width="15"
                      height="15"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      />
                    </svg>

                    <span>
                      {r.fromDate} → {r.toDate} · {r.totalDays} working day
                      {r.totalDays > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="leave-history-reason">{r.reason}</div>
                  {r.medicalCertificateData && (
                    <div style={{ marginTop: "0.35rem" }}>
                      <a
                        href={r.medicalCertificateData}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "0.8rem", color: "#2563eb", textDecoration: "underline", fontWeight: 500 }}
                      >
                        📄 View Medical Certificate ({r.medicalCertificateName || "Document"})
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}